import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Warning, Barcode } from "@phosphor-icons/react";
import { amazonUrl } from "@/lib/links";
import { AsinSweep } from "@/components/admin/AsinSweep";

const money = (n) => n == null ? "—" : `$${Number(n).toFixed(2)}`;

/** Sets whose Amazon price is wildly off PriceCharting — almost always the wrong ASIN (light kit, minifig…). */
export default function Anomalies() {
  const [data, setData] = useState(null);
  const [showReviewed, setShowReviewed] = useState(false);
  const [asinDraft, setAsinDraft] = useState({});
  const [busy, setBusy] = useState(null);
  const [tab, setTab] = useState(window.location.hash === "#sweep" ? "sweep" : "anomalies");
  const load = () => api.anomalies(showReviewed).then(setData).catch(() => toast.error("Failed to load anomalies"));
  useEffect(() => { load(); }, [showReviewed]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (sn, action, asin) => {
    setBusy(sn);
    try {
      await api.resolveAnomaly(sn, { action, asin });
      toast.success(action === "ok" ? `${sn} marked OK` : action === "clear_asin" ? `${sn}: ASIN cleared — Amazon data wiped, will re-resolve on next scan` : `${sn}: ASIN set to ${asin} — rescan to refresh prices`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(null);
  };
  const Tab = ({ id, icon: Icon, label, count }) => (
    <button onClick={() => { setTab(id); window.location.hash = id === "sweep" ? "sweep" : ""; }}
      className={`h-9 px-4 inline-flex items-center gap-2 rounded-sm font-head font-bold uppercase text-xs tracking-wider border ${tab === id ? "bg-black text-white border-black" : "border-[#E4E4E0] hover:border-black"}`} data-testid={`asin-tab-${id}`}>
      <Icon size={14} weight="bold" /> {label}{count != null ? <span className="font-mono text-[10px] opacity-70">{count}</span> : null}
    </button>
  );
  return (
    <div className="p-6 space-y-4" data-testid="anomalies-page">
      <div className="flex items-center gap-2 flex-wrap">
        <Tab id="anomalies" icon={Warning} label="Price anomalies" count={data?.count} />
        <Tab id="sweep" icon={Barcode} label="Suspect ASIN sweep" />
      </div>
      {tab === "sweep" ? <AsinSweep /> : (<>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-head text-2xl font-black uppercase tracking-tighter flex items-center gap-2"><Warning size={22} weight="fill" className="text-[#D97706]" /> Price anomalies</h1>
          <p className="font-mono text-xs text-gray-600 mt-1">
            Amazon price ÷ PriceCharting outside {data?.thresholds?.low ?? 0.4}× – {data?.thresholds?.high ?? 2.5}×. Usually the ASIN points at a light kit, minifigure or bundle.
            Fix the ASIN (or clear it) and the cached Amazon numbers are wiped so Deals, the board and public quotes re-fetch. Public quotes already ignore anomalous Amazon prices automatically.
          </p>
        </div>
        <label className="flex items-center gap-2 font-mono text-xs text-gray-600">
          <input type="checkbox" checked={showReviewed} onChange={(e) => setShowReviewed(e.target.checked)} data-testid="anomalies-show-reviewed" /> show reviewed
        </label>
      </div>
      <div className="border border-[#E4E4E0] rounded-sm overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead className="bg-[#F6F6F3] text-gray-500 uppercase text-[10px]"><tr>
            <th className="px-3 py-2 text-left">Set</th><th className="px-3 py-2 text-left">Catalog name</th><th className="px-3 py-2 text-left">Amazon listing we matched</th>
            <th className="px-3 py-2 text-right">Amazon</th><th className="px-3 py-2 text-right">PriceCharting</th><th className="px-3 py-2 text-right">Ratio</th><th className="px-3 py-2 text-left">Fix</th>
          </tr></thead>
          <tbody>
            {!data ? <tr><td colSpan={7} className="p-6 text-center text-gray-500">Loading…</td></tr> :
             data.rows.length === 0 ? <tr><td colSpan={7} className="p-6 text-center text-gray-500">No anomalies — every Amazon price is within range of PriceCharting.</td></tr> :
             data.rows.map(r => (
              <tr key={r.set_number} className={`border-t border-[#E4E4E0] ${r.reviewed ? "opacity-60" : ""}`} data-testid={`anomaly-${r.set_number}`}>
                <td className="px-3 py-2 font-bold"><Link to={`/sets/${r.set_number}`} className="text-[#E3000B] hover:underline">{r.set_number}</Link><div className="text-[10px] text-gray-500">{r.theme}{r.year ? ` · ${r.year}` : ""}</div></td>
                <td className="px-3 py-2 text-gray-900 max-w-[220px]">{r.name || "—"}</td>
                <td className="px-3 py-2 max-w-[320px]">
                  <div className="truncate text-gray-800" title={r.amazon_title || ""}>{r.amazon_title || <span className="text-gray-400">title unknown</span>}</div>
                  {(r.amazon_asin || r.catalog_asin) && <a href={amazonUrl(r.amazon_asin || r.catalog_asin)} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#0055BF] hover:underline">{r.amazon_asin || r.catalog_asin} ↗</a>}
                </td>
                <td className="px-3 py-2 text-right text-gray-900">{money(r.buy_box)}</td>
                <td className="px-3 py-2 text-right text-gray-900">{money(r.pc_sealed)}</td>
                <td className={`px-3 py-2 text-right font-bold ${r.kind === "amazon_too_low" ? "text-[#E3000B]" : "text-[#D97706]"}`} data-testid={`anomaly-ratio-${r.set_number}`}>{r.ratio}×<div className="text-[9px] font-normal text-gray-500">{r.kind === "amazon_too_low" ? "Amazon way under PC" : "Amazon way over PC"}</div></td>
                <td className="px-3 py-2">
                  {r.reviewed ? (
                    <div className="flex items-center gap-2 text-[10px] text-gray-500">reviewed · {r.reviewed.action}{r.reviewed.asin ? ` ${r.reviewed.asin}` : ""}
                      <button onClick={() => api.unreviewAnomaly(r.set_number).then(load)} className="underline" data-testid={`anomaly-unreview-${r.set_number}`}>undo</button></div>
                  ) : (
                    <div className="flex items-center gap-1 flex-wrap">
                      <Input placeholder="correct ASIN" value={asinDraft[r.set_number] || ""} onChange={(e) => setAsinDraft(d => ({ ...d, [r.set_number]: e.target.value }))}
                        className="h-7 w-28 bg-[#F6F6F3] border-[#E4E4E0] text-xs" data-testid={`anomaly-asin-${r.set_number}`} />
                      <button disabled={busy === r.set_number || !(asinDraft[r.set_number] || "").trim()} onClick={() => act(r.set_number, "set_asin", asinDraft[r.set_number].trim())}
                        className="h-7 px-2 border border-[#00A850]/50 text-[#00A850] rounded-sm hover:bg-[#00A850]/10 disabled:opacity-40" data-testid={`anomaly-set-asin-${r.set_number}`}>SET ASIN</button>
                      <button disabled={busy === r.set_number} onClick={() => act(r.set_number, "clear_asin")}
                        className="h-7 px-2 border border-[#E3000B]/50 text-[#E3000B] rounded-sm hover:bg-[#E3000B]/10" data-testid={`anomaly-clear-${r.set_number}`}>CLEAR ASIN</button>
                      <button disabled={busy === r.set_number} onClick={() => act(r.set_number, "ok")}
                        className="h-7 px-2 border border-[#E4E4E0] text-gray-600 rounded-sm hover:border-black" data-testid={`anomaly-ok-${r.set_number}`}>IT'S CORRECT</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>)}
    </div>
  );
}
